/* eslint-disable */
import { cast, flow, Instance, SnapshotIn, types } from 'mobx-state-tree';
import Resource, { IResource } from '../Models/Resource';

export const Partner = types
  .model({
    partnerId: types.string, // groupCode and partnerId are synonymous
    backgroundColor: types.optional(types.string, ''),
    navigationBarColor: types.optional(types.string, ''),
    fontColor: types.optional(types.string, ''),
    resources: types.optional(types.array(Resource), []),
  })
  .views((self) => ({
    getResourcesKeys() {
      return self.resources.reduce((resourcesObject: any, resource: { assessmentKey: string | number; }) => {
        resourcesObject[resource.assessmentKey] = resource;
        return resourcesObject;
      }, {});
    },
    getMatchingIndex(key: string) {
      const keys = this.getResourcesKeys();
      const index = Object.keys(keys).indexOf(key);
      return index;
    },
  }))
  .actions((self) => ({
    setResources(resources: IResource[]) {
      self.resources.clear();
      self.resources.push(...resources);
    },
    setFontColor(fontColor: string) {
      self.fontColor = fontColor;
    },
    setBackgroundColor(backgroundColor: string) {
      self.backgroundColor = backgroundColor;
    },
    setNavigationBarColor(navigationBarColor: string) {
      self.navigationBarColor = navigationBarColor;
    },
    setPartnerId(partnerId: string){
      self.partnerId = partnerId
    }
  }));

export const PartnerStore = types
  .model('PartnerStore', {
    partnersByIdMap: types.map(Partner),
    logosByPartnerId: types.map(types.string),
    isLoading: types.boolean,
  })
  .actions((self) => {
    const wrapLoadingDuringAction = flow(function* (serviceAction: any) {
      self.isLoading = true;
      try {
        yield serviceAction();
      } catch (err) {
        console.error(err);
      }
      self.isLoading = false;
    });

    return {
      loadLogo: flow(function* (partnerId: string | undefined | null): any {
        yield wrapLoadingDuringAction(
          flow(function* () {
            if (!partnerId) {
              return
            }
            const logo = yield getLogoAsync(partnerId);
            self.logosByPartnerId.set(partnerId, logo);
          })
        );
      }),
      setLogo: flow(function* (logoEncodedString: string, partnerId: string) {
        yield wrapLoadingDuringAction(
          flow(function* () {
            yield setLogoAsync(logoEncodedString, partnerId);
          })
        );
      }),
      deleteLogo: flow(function* (partnerId: string | undefined | null) {
        yield wrapLoadingDuringAction(
          flow(function* () {
            if (!partnerId) {
              return;
            }
            const logo = yield deleteLogoAsync(partnerId);
            if (logo.status === 204) {
              self.logosByPartnerId.set(partnerId, '');
            }
          })
        );
      }),
      savePartner: flow(function* (partner: IPartner) {
        yield wrapLoadingDuringAction(
          flow(function* () {
            const partnerSnapshot: IPartnerSnapshotIn = {
              partnerId: partner.partnerId,
              resources: partner.resources,
              fontColor: undefined,
              navigationBarColor: undefined,
              backgroundColor: undefined
            };
            return yield upsertPartnerAsync(partnerSnapshot);
          })
        );
      }),
      loadPartner: flow(function* (partnerId: string) {
        yield wrapLoadingDuringAction(
          flow(function* () {
            const retrievedPartner = yield getPartnerAsync(partnerId);
            self.partnersByIdMap.set(
              retrievedPartner.partnerId,
              retrievedPartner
            );
          })
        );
      }),
    };
  });

async function getLogoAsync(partnerId: string): Promise<string> {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const logoBucketName = process.env.REACT_APP_LOGOS_BUCKET_NAME;
  const rawBlobResponse = await fetch(
    `${baseUrl}/blobs/${logoBucketName}/${partnerId}`
  );

  if (rawBlobResponse.status === 404) {
    return '';
  }

  const blobResponse = await rawBlobResponse.json();
  const base64ImageString = `${blobResponse}`;

  return base64ImageString;
}

async function setLogoAsync(
  logoEncodedString: string,
  partnerId: string
): Promise<void> {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const logoBucketName = process.env.REACT_APP_LOGOS_BUCKET_NAME;

  const uploadLogoRequest = {
    blobBucketName: logoBucketName,
    fileName: partnerId,
    contents: logoEncodedString,
  };
  await fetch(`${baseUrl}/blobs`, {
    method: 'put',
    body: JSON.stringify(uploadLogoRequest),
  });
}

async function deleteLogoAsync(partnerId: string): Promise<Response> {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const logoBucketName = process.env.REACT_APP_LOGOS_BUCKET_NAME;
  const response = await fetch(
    `${baseUrl}/blobs/${logoBucketName}/${partnerId}`,
    { method: 'delete' }
  );
  return response;
}

export async function getPartnerAsync(partnerId: string,groupCodeCheck:boolean = false): Promise<IPartnerSnapshotIn> {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const response = await fetch(`${baseUrl}/partners/${partnerId}`);

  if (response.status === 404) {
    return {
      partnerId: groupCodeCheck ? null : partnerId,
      backgroundColor: '',
      navigationBarColor: '',
      fontColor: '',
      resources: [],
    };
  }

  const parsedResponse = await response.json();
  return {
    partnerId: partnerId,
    backgroundColor: parsedResponse.backgroundColor ?? '',
    navigationBarColor: parsedResponse.navigationBarColor ?? '',
    fontColor: parsedResponse.fontColor ?? '',
    resources: parsedResponse.resources ?? [],
  };
}

async function upsertPartnerAsync(partner: IPartnerSnapshotIn): Promise<void> {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const partnerUpsertRequest = {
    backgroundColor: partner.backgroundColor,
    navigationBarColor: partner.navigationBarColor,
    fontColor: partner.fontColor,
    resources: partner.resources,
  };
  await fetch(`${baseUrl}/partners/${partner.partnerId}`, {
    method: 'put',
    body: JSON.stringify(partnerUpsertRequest),
  });
}

export interface IPartnerStore extends Instance<typeof PartnerStore> {
    partnersByIdMap: Array<typeof Partner> | any;
    loadLogo(partnerId: any): any;
    setLogo(arg0: string, partnerId: any): Promise<any>;
    deleteLogo(partnerCode: any): any;
    savePartner(partner: IPartner): Promise<any>;
}
export interface IPartner extends Instance<typeof Partner> {
    resources: any;
    partnerId: any;
    setBackgroundColor(hex: any): unknown;
    setNavigationBarColor(hex: any): unknown;
    setFontColor(hex: any): unknown;
    backgroundColor: any;
    navigationBarColor: any;
    fontColor: any;
}
export interface IPartnerSnapshotIn extends SnapshotIn<typeof Partner> {
  partnerId: any;
  resources: any;
  fontColor: any;
  navigationBarColor: any;
  backgroundColor: any;
}
